<script setup lang="ts">
// This starter template is using Vue 3 <script setup> SFCs
// Check out https://v3.vuejs.org/api/sfc-script-setup.html#sfc-script-setup
import router from '@/router'
import { UIContentWrap } from '@gohighlevel/ghl-ui'
import 'driver.js/dist/driver.css'
import 'moment/dist/locale/da'
import 'moment/dist/locale/de'
import 'moment/dist/locale/en-gb'
import 'moment/dist/locale/es'
import 'moment/dist/locale/fi'
import 'moment/dist/locale/fr'
import 'moment/dist/locale/fr-ca'
import 'moment/dist/locale/hr'
import 'moment/dist/locale/hu'
import 'moment/dist/locale/it'
import 'moment/dist/locale/nb'
import 'moment/dist/locale/nl'
import 'moment/dist/locale/pt'
import 'moment/dist/locale/pt-br'
import 'moment/dist/locale/sv'

import Postmate from 'postmate'
import { computed, onMounted } from 'vue'
import AppState, {
  fetchCalendarLocationSettings,
  fetchRequiredData,
  getCalendarsInLocation,
  getEquipmentsInLocation,
  getRoomsInLocation,
  getServiceMenusInLocation,
  getTeamsInLocation,
  initPendo,
  setApiKey,
  setAuthApiKey,
  setBaseUrl,
  setCompanyId,
  setCustomObjectLabels,
  setDisableContactTImezone,
  setEmbedUrl,
  setIsCouponEnabled,
  setIsLoading,
  setIsRecurringEnabled,
  setIsScheduleEnabled,
  setIsServicesEnabled,
  setIsSmsNotificationsEnabled,
  setIsUserPartOfLocation,
  setIsWhatsappNotificationsEnabled,
  setLocale,
  setLocationId,
  setLocationTimezone,
  setLoggedInUserDetail,
  setMediaFileURI,
  setParentApp,
  setTokenId,
  setUser,
  setUserPreferenceTimezone,
  setWhitelabelDomain,
  toggleChangesSaved
} from './states/app'
import { setSentryMeta } from './util/setupSentry'
import { startDriverGuide } from './utils/tour_guide.util'

import { initRemoteApps } from './config/remoteApps'

initRemoteApps()

function openParentConnection() {
  const handshake = new Postmate.Model({
    changeChildAppRoute(route: any) {
      AppState.exitTour = true
      router.push(route)
    },
    setTokenId: (tokenId: string) => {
      setTokenId(tokenId)
    },
    setMediaFile: (file: any) => {
      if (file && file.url) {
        setMediaFileURI(file.url)
      }
    },
    closeTextSnippetModal: (data) => {
      AppState.showTextSnippetModal = false
      if (data.success) {
        AppState.fetchSmsTemplates = true
      }
    },
    goToCalendarSettingsPage: () => {
      router.push({ name: 'calendar-settings__default' })
    },
    whatsappTemplateCreated:(data: any) => {
      const { templateId } = data
      AppState.whatsappNotificationTemplate.templateId = templateId
      AppState.whatsappNotificationTemplate.isUpdated = true
    },
    refreshCalendars: async () => {
      setIsLoading(true)
      await fetchCalendarLocationSettings()
      await getCalendarsInLocation()
      await getTeamsInLocation()
      await getServiceMenusInLocation()
      await getRoomsInLocation()
      await getEquipmentsInLocation()
      setIsLoading(false)
    },
    refreshAppointments: () => {
      toggleChangesSaved()
    },
    appointmentList: (data: any) => {
      setLocationId(data.locationId)
      router.push({ name: 'appointments-list' })
    },
    reinitializeTourGuide: (data: {
      tourSteps: string
      currentTourStep: number
    }) => {
      startDriverGuide(
        { ...data, locationId: AppState.locationId },
        AppState.parentApp,
        router
      )
    },
  })

  handshake.then((parent: any) => {
    setParentApp(handshake)
    const { model } = parent
    const {
      locationId,
      source,
      tokenId,
      apiKey,
      isUserPartOfLocation,
      authApiKey,
      userId,
      userType,
      userRole,
      timeZone,
      locale,
      whitelabelUrl,
      disableContactTimezone,
      baseUrl,
      embedUrl,
      company,
      recurringEventsEnabled,
      userPreferenceTimezone,
      isServicesEnabled,
      isCouponEnabled,
      loggedInUser,
      customObjectLabels,
      isScheduleEnabled,
      isSmsNotificationsEnabled,
      isWhatsappNotificationsEnabled,
    } = model
    setSentryMeta({
      userId: model.userId,
      companyId: model.company.id,
      locationId: model.locationId,
      locale: model.locale,
    })
    setIsUserPartOfLocation(isUserPartOfLocation)
    setApiKey(apiKey)
    setDisableContactTImezone(disableContactTimezone)
    setBaseUrl(baseUrl)
    setEmbedUrl(embedUrl)
    setCompanyId(company?.id)
    setAuthApiKey(authApiKey)
    setWhitelabelDomain(whitelabelUrl)
    setLocationTimezone(timeZone)
    setLocale(locale)
    setTokenId(tokenId)
    setUser({
      id: userId,
      type: userType,
      role: userRole,
      version: 1,
    })
    setLocationId(locationId)
    setIsRecurringEnabled(recurringEventsEnabled)
    setUserPreferenceTimezone(userPreferenceTimezone)
    setIsServicesEnabled(isServicesEnabled)
    setIsCouponEnabled(isCouponEnabled)
    setLoggedInUserDetail(loggedInUser)
    setCustomObjectLabels(customObjectLabels)
    setIsScheduleEnabled(isScheduleEnabled)
    setIsSmsNotificationsEnabled(isSmsNotificationsEnabled)
    setIsWhatsappNotificationsEnabled(isWhatsappNotificationsEnabled)
    try {
      initPendo()
    } catch (error) {
      console.error('Calendar app: Pendo not initialized', error)
    }

    try {
      startDriverGuide(model, parent, router)
    } catch (err) {
      console.error('error initializing tour guide', err)
    }

    parent.emit('get-loggedin-user')
    parent.emit('get-location')

    if (source === 'calendar-setup') {
      fetchRequiredData(false)
      if (router.currentRoute.value.path.includes('calendars/preferences')) {
        router.push({ name: 'calendar-settings__preferences' })
      } else if (
        router.currentRoute.value.path.includes('calendars/availability')
      ) {
        router.push({ name: 'calendar-settings__availability' })
      } else if (
        router.currentRoute.value.path.includes('calendars/connections')
      ) {
        router.push({ name: 'calendar-settings__connections' })
      } else {
        router.push({ name: 'calendar-settings__default' })
      }
    } else if (source === 'appointments_page') {
      router.push({
        name: 'appointments-list',
        params: { locationId: locationId },
      })
    } else {
      router.push({ name: 'select-contact' })
    }
  })
}

const locale = computed(() => {
  return AppState?.locale?.replace('_', '')
})

onMounted(() => {
  openParentConnection()
})
</script>

<template>
  <div class="bg-gray-25">
    <UIContentWrap :full-screen="true" :locale="locale">
      <router-view />
    </UIContentWrap>
  </div>
</template>

<style>
@import './tour_guide.css';
</style>
